import {
  input as angularInput,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  datepicker,
  DynamicFormField,
  FieldBuilderBase,
  fieldGroup,
  formBuilder,
  input,
  number,
  select,
} from '@kinet-open-network/client/core/forms/ui';
import {
  AutomationContext,
  AutomationRuleOperator,
  automationRuleOperatorOptions,
  AutomationTriggerFieldChangeRuleType,
  createEnumList,
} from '@kinet-open-network/shared/data-access/model';
import { FormlyModule } from '@ngx-formly/core';
import { AUTOMATION_BUILDER_CONFIG } from '../../automation-builder.config';
import { IFieldChangeRule } from '../field-change-trigger.service';
import {
  allowedOperatorsByFieldType,
  fieldChangeContextFields,
  FieldChangeFieldType,
  IFieldChangeFieldDef,
} from './field-change-context-fields';

export const FIELD_CHANGE_RULE_TEMPLATE = `
  <formly-form
    [fields]="fields()"
    [model]="model()"
    (modelChange)="handleModelChange($event)"
  />
`;

@Component({
  selector: 'kon-field-change-rule',
  imports: [FormlyModule],
  template: FIELD_CHANGE_RULE_TEMPLATE,
})
export class FieldChangeRuleComponent implements OnInit {
  readonly #config = inject(AUTOMATION_BUILDER_CONFIG, { optional: true });

  context = angularInput<AutomationContext | undefined>();
  rule = angularInput<Partial<IFieldChangeRule>>();

  ruleChange = output<Partial<IFieldChangeRule>>();

  readonly model = signal<Partial<IFieldChangeRule>>({});

  readonly key = computed(() => this.model().key);

  readonly fields = computed(() => {
    const ctx = this.context();
    const key = this.key();
    return this.buildFields(ctx, key);
  });

  readonly #contextFields = computed(() => ({
    ...fieldChangeContextFields,
    ...this.#config?.fieldChangeContextFields,
  }));

  constructor() {
    effect(() => {
      const ruleValue = this.rule();
      if (ruleValue && Object.keys(ruleValue).length > 0) {
        this.model.set(this.extractModel(ruleValue));
      }
    });
  }

  ngOnInit(): void {
    const ruleValue = this.rule() || {};
    if (Object.keys(ruleValue).length > 0) {
      this.model.set(this.extractModel(ruleValue));
    }
  }

  handleModelChange(data: Partial<IFieldChangeRule>) {
    // Compare against the input rule (source of truth), not the model which may be already updated
    const baseRule = this.rule() || {};
    const prevKey = baseRule.key;
    const newKey = data.key;
    const context = this.context();

    const contextFields = context ? (this.#contextFields()[context] ?? []) : [];
    const newFieldDef = newKey
      ? contextFields.find((f) => f.key === newKey)
      : undefined;
    const prevFieldDef = prevKey
      ? contextFields.find((f) => f.key === prevKey)
      : undefined;

    const shouldReset =
      newFieldDef?.type !== prevFieldDef?.type ||
      (newFieldDef?.type === 'enum' && newKey !== prevKey);

    if (shouldReset) {
      // Reset operator and value when type changes, fields will auto-rebuild through computed
      const resetData = { ...data, operator: undefined, value: null };
      this.model.set(resetData);
      // Emit the reset data so parent knows about the change
      this.ruleChange.emit({ ...baseRule, ...resetData });
      return;
    }

    this.model.set(data);
    this.ruleChange.emit({ ...baseRule, ...data });
  }

  private extractModel(
    ruleValue: Partial<IFieldChangeRule>,
  ): Partial<IFieldChangeRule> {
    return Object.entries(ruleValue).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        acc[key as keyof IFieldChangeRule] = value;
      }
      return acc;
    }, {} as Partial<IFieldChangeRule>);
  }

  private buildFields(
    context: AutomationContext | undefined,
    key: string | undefined,
  ): DynamicFormField[] {
    const contextFields = context ? (this.#contextFields()[context] ?? []) : [];
    const fieldDef = key ? contextFields.find((f) => f.key === key) : undefined;

    const keyOptions = contextFields.map((f) => ({
      label: f.label,
      value: f.key,
    }));

    const operatorOptions = this.buildOperatorOptions(fieldDef);
    const valueField = this.buildValueField(fieldDef);

    return formBuilder(
      fieldGroup()
        .fieldGroupClassName('grid grid-cols-4 gap-2')
        .fieldGroup([
          select('rule', 'Typ hodnoty')
            .options([
              {
                label: 'Stará hodnota',
                value: AutomationTriggerFieldChangeRuleType.oldValue,
              },
              {
                label: 'Nová hodnota',
                value: AutomationTriggerFieldChangeRuleType.newValue,
              },
            ])
            .required()
            .wrappers([]),
          select('key', 'Pole').options(keyOptions).required().wrappers([]),
          select('operator', '')
            .options(operatorOptions)
            .required()
            .wrappers([]),
          valueField.required(),
        ]),
    );
  }

  private buildOperatorOptions(
    fieldDef: IFieldChangeFieldDef | undefined,
  ): { label: string; value: string }[] {
    if (!fieldDef) {
      return createEnumList(automationRuleOperatorOptions).filter(
        (op) =>
          op.value !== AutomationRuleOperator.GreaterThan &&
          op.value !== AutomationRuleOperator.GreaterThanOrEquals &&
          op.value !== AutomationRuleOperator.LessThan &&
          op.value !== AutomationRuleOperator.LessThanOrEquals,
      ) as { label: string; value: string }[];
    }
    return allowedOperatorsByFieldType[fieldDef.type].map((op) => ({
      label: automationRuleOperatorOptions.get(op)?.label ?? op,
      value: op,
    }));
  }

  private buildValueField(
    fieldDef: IFieldChangeFieldDef | undefined,
  ): FieldBuilderBase {
    const hideExpr = `model.operator === '${AutomationRuleOperator.IsSet}' || model.operator === '${AutomationRuleOperator.IsNotSet}'`;
    const type: FieldChangeFieldType = fieldDef?.type ?? 'string';

    switch (type) {
      case 'number':
        return number('value', 'Hodnota')
          .props({ placeholder: 'Zadajte hodnotu', min: undefined as never })
          .wrappers([])
          .hide(hideExpr, true);

      case 'boolean':
        return select('value', '')
          .options([
            { label: 'Áno', value: true },
            { label: 'Nie', value: false },
          ])
          .wrappers([])
          .hide(hideExpr, true);

      case 'date':
        return datepicker('value', 'Hodnota').wrappers([]).hide(hideExpr, true);

      case 'enum':
        return select('value', '')
          .options(
            (fieldDef?.options as { label: string; value: string }[]) ?? [],
          )
          .wrappers([])
          .hide(hideExpr, true);

      default:
        return input('value', 'Hodnota')
          .props({ placeholder: 'Zadajte hodnotu' })
          .wrappers([])
          .hide(hideExpr, true);
    }
  }
}
